/**
 * Sidebar tab panel that previews Candid document write-backs.
 *
 * Tabs come from `groupedByUseCase.document`. PDF vs image is chosen
 * from the GCS path extension on {@link CandidDocumentPreviewTab.src}.
 *
 * @module candidRpaDocumentPreview
 */
import { SidebarTabPanel } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { CandidDocumentPreviewTab } from "../../../pages/worklists/singleOrder/candidUpload/types/candidDocumentPreviewTab";
import { CandidWritebackRecord } from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { CandidWritebacksByUseCase } from "../../../pages/worklists/singleOrder/candidUpload/utils/groupCandidWritebacksByUseCase";
import { RootState } from "../../../redux/store/store";
import CandidRpaDocumentPreviewPanel from "./candidRpaDocumentPreviewPanel";
import { mapDocumentWritebacksToPreviewTabs } from "./mapDocumentWritebacksToPreviewTabs";

/**
 * Reads document write-backs from modal metadata.
 *
 * @param state - Redux root state
 * @returns Document write-back rows, or an empty list
 */
const selectDocumentWritebacks = (
  state: RootState,
): CandidWritebacksByUseCase["document"] => {
  const grouped = state.modal.metaData?.groupedByUseCase as
    CandidWritebacksByUseCase | undefined;
  return grouped?.document ?? [];
};

/**
 * Reads the write-back row that opened the modal, when present.
 *
 * @param state - Redux root state
 * @returns The clicked table row, or undefined
 */
const selectRowData = (state: RootState): CandidWritebackRecord | undefined =>
  (state.modal.metaData?.rowData as CandidWritebackRecord | undefined) ??
  undefined;

/**
 * Opens the tab for the clicked document row, else the first tab.
 *
 * @param selectedRow - Write-back that opened the modal, if any
 * @param tabs - Document preview tabs
 * @returns Tab id
 */
const resolveDefaultDocumentTab = (
  selectedRow: CandidWritebackRecord | undefined,
  tabs: CandidDocumentPreviewTab[],
): string => {
  if (selectedRow?.id && tabs.some((tab) => tab.id === selectedRow.id)) {
    return selectedRow.id;
  }
  return tabs[0]?.id ?? "";
};

/**
 * Document sidenav: one tab per document write-back.
 *
 * @returns Sidebar tab panel for Candid document previews
 */
const CandidRpaDocumentPreview = () => {
  const documentRecords = useSelector(selectDocumentWritebacks);
  const selectedRow = useSelector(selectRowData);
  const tabs = useMemo(
    () => mapDocumentWritebacksToPreviewTabs(documentRecords),
    [documentRecords],
  );
  const defaultTab = useMemo(
    () => resolveDefaultDocumentTab(selectedRow, tabs),
    [selectedRow, tabs],
  );

  return (
    <div className="content min-h-0 flex-1 overflow-hidden">
      <SidebarTabPanel
        activeTextColor="#0056D6"
        activeBorderColor="#0056D6"
        className="candid-rpa-status-content"
        sidebarWidth="25%"
        defaultValue={defaultTab}
        tabs={tabs.map((tab) => ({
          id: tab.id,
          label: tab.label,
        }))}
      >
        {(activeTabId) => {
          const activeTab = tabs.find((tab) => tab.id === activeTabId);
          if (!activeTab) {
            return null;
          }
          return (
            <CandidRpaDocumentPreviewPanel key={activeTab.id} tab={activeTab} />
          );
        }}
      </SidebarTabPanel>
    </div>
  );
};

export default CandidRpaDocumentPreview;
